/**
 * AdminDashboard.js (v3 — Modular Tabs)
 *
 * Raider Depot — Admin Dashboard
 * Route: /admin (Firebase Email/Password auth required)
 *
 * Thin shell: auth check, login screen, tab navigation.
 * Each tab is a standalone component in ./admin/
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  getIdTokenResult,
} from "firebase/auth";

import S from "./admin/adminStyles";
import MfaEnrollment from "./admin/MfaEnrollment";
import OrdersTab from "./admin/OrdersTab";
import CatalogTab from "./admin/CatalogTab";
import InventoryTab from "./admin/InventoryTab";
import ClientsTab from "./admin/ClientsTab";
import AdminUsersTab from "./admin/AdminUsersTab";
import MetricsTab from "./admin/MetricsTab";
import FeedbackTab from "./admin/FeedbackTab";
import CyclesTab from "./admin/CyclesTab";
import EmergencyOrdersTab from "./admin/EmergencyOrdersTab";
import TokensTab from "./admin/TokensTab";
import SupplyLedgerTab from "./admin/SupplyLedgerTab";

const TAB_CONFIG = [
  { id: "orders", label: "\ud83d\udce6 Orders", component: OrdersTab },
  {
    id: "catalog",
    label: "\ud83d\udccb Catalog",
    component: CatalogTab,
    roles: ["system", "operations"],
  },
  {
    id: "inventory",
    label: "\ud83c\udfea Inventory",
    component: InventoryTab,
    roles: ["system", "operations"],
  },
  {
    id: "clients",
    label: "\ud83d\udc65 Clients",
    component: ClientsTab,
    roles: ["system", "counselor"],
  },
  {
    id: "emergency",
    label: "\ud83d\udea8 Emergency",
    component: EmergencyOrdersTab,
    roles: ["system", "counselor"],
  },
  {
    id: "tokens",
    label: "\ud83c\udfab Tokens",
    component: TokensTab,
    roles: ["system", "counselor"],
  },
  {
    id: "users",
    label: "👥 Users",
    component: AdminUsersTab,
    roles: ["system"],
  },
  { id: "metrics", label: "\ud83d\udcca Metrics", component: MetricsTab, roles: ["system", "operations"] },
  {
    id: "feedback",
    label: "\ud83d\udcac Feedback",
    component: FeedbackTab,
    roles: ["system", "operations"],
  },
  {
    id: "cycles",
    label: "\ud83d\udd04 Cycles",
    component: CyclesTab,
    roles: ["system", "operations"],
  },
  {
    id: "ledger",
    label: "\ud83d\udccb Ledger",
    component: SupplyLedgerTab,
    roles: ["system", "operations"],
  },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");
  const [adminRole, setAdminRole] = useState(null);

  const adminRoleLower = adminRole ? adminRole.toLowerCase() : null;
  const visibleTabs = TAB_CONFIG.filter((tab) => {
    return !tab.roles || tab.roles.includes(adminRoleLower);
  });

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "orders");
    }
  }, [activeTab, visibleTabs]);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u && !u.isAnonymous) {
        setUser(u);
        try {
          const idTokenResult = await getIdTokenResult(u);
          const role = idTokenResult.claims.admin_role;
          setAdminRole(role ? role.charAt(0).toUpperCase() + role.slice(1) : 'No role assigned');
        } catch (error) {
          console.error('Error fetching ID token result:', error);
          setAdminRole('No role assigned');
        }
      } else {
        setUser(null);
        setAdminRole(null);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      setLoginError("");
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
        setLoginError("");
      } else {
        setLoginError("Invalid email or password.");
      }
    }
  };

  const handleMfaVerify = async () => {
    try {
      if (!mfaResolver) return;
      const totpHint = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );
      if (!totpHint) {
        setLoginError("TOTP not configured.");
        return;
      }
      const assertion = await TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        mfaCode,
      );
      await mfaResolver.resolveSignIn(assertion);
      setMfaResolver(null);
      setMfaCode("");
    } catch (err) {
      setLoginError("Invalid verification code.");
      setMfaCode("");
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setLoginError("Please enter your email address first.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      setResetSent(true);
      setLoginError("");
    } catch (err) {
      // Don't reveal whether email exists
      setResetSent(true);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  if (authLoading) {
    return (
      <div style={{ ...S.loginContainer }}>
        <p>Loading...</p>
      </div>
    );
  }

  // ── Login Screen ──────────────────────────────────────────────────
  if (!user) {
    return (
      <div style={S.loginContainer}>
        <div style={S.loginCard}>
          <h1
            style={{ fontSize: "22px", color: "#1e293b", marginBottom: "4px" }}
          >
            Raider Depot Admin
          </h1>
          <p
            style={{ fontSize: "13px", color: "#94a3b8", marginBottom: "24px" }}
          >
            Sign in to manage the food pantry
          </p>
          {mfaResolver ? (
            <>
              <p
                style={{
                  fontSize: "13px",
                  color: "#475569",
                  marginBottom: "12px",
                }}
              >
                Enter your TOTP code from your authenticator app:
              </p>
              <input
                style={{ ...S.input, marginBottom: "12px" }}
                type="text"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) =>
                  setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyDown={(e) => e.key === "Enter" && handleMfaVerify()}
              />
              {loginError && (
                <p style={{ color: "#dc2626", fontSize: "13px" }}>
                  {loginError}
                </p>
              )}
              <button
                style={{ ...S.btnPrimary, width: "100%", marginTop: "8px" }}
                onClick={handleMfaVerify}
              >
                Verify
              </button>
              <button
                onClick={() => {
                  setMfaResolver(null);
                  setMfaCode("");
                  setLoginError("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3b82f6",
                  fontSize: "13px",
                  cursor: "pointer",
                  textDecoration: "underline",
                  marginTop: "8px",
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <input
                style={{ ...S.input, marginBottom: "12px" }}
                type="email"
                placeholder="admin@riverwoodraiderdepot.org"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
              <input
                style={{ ...S.input, marginBottom: "12px" }}
                type="password"
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              />
              {loginError && (
                <p style={{ color: "#dc2626", fontSize: "13px" }}>
                  {loginError}
                </p>
              )}
              {resetSent && (
                <p style={{ color: "#16a34a", fontSize: "13px" }}>
                  If an account exists with that email, a password reset link
                  has been sent.
                </p>
              )}
              <button
                style={{ ...S.btnPrimary, width: "100%", marginTop: "8px" }}
                onClick={handleLogin}
              >
                Sign In
              </button>
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                <button
                  onClick={handleForgotPassword}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#3b82f6",
                    fontSize: "13px",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Authenticated Dashboard ───────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={S.topBar}>
        <div>
          <h1 style={S.topBarTitle}>Raider Depot Admin</h1>
          <span style={S.topBarUser}>{user.email}{adminRole ? ` (${adminRole})` : ''}</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            style={S.signOutBtn}
            onClick={() => navigate("/")}
          >
            🏠 Home
          </button>
          <button
            style={S.signOutBtn}
            onClick={() => setShowMfaEnroll(true)}
          >
            🔑 MFA
          </button>
          <button style={S.signOutBtn} onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
      {showMfaEnroll && (
        <MfaEnrollment user={user} onClose={() => setShowMfaEnroll(false)} />
      )}

      {/* Tabs */}
      <div style={S.tabs}>
        {visibleTabs.map(({ id, label }) => (
          <button
            key={id}
            style={{ ...S.tab, ...(activeTab === id ? S.tabActive : {}) }}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={S.content}>
        {TAB_CONFIG.filter(
          (t) =>
            t.component &&
            t.id === activeTab &&
            (!t.roles || t.roles.includes(adminRoleLower)),
        ).map(({ id, component: TabComponent }) => (
          <TabComponent key={id} user={user} auth={auth} />
        ))}
      </div>
    </div>
  );
}
